import React from "react";
import { Alert, Descriptions, Tag } from "antd";
import type { DescriptionsProps } from "antd";
import DetailDrawer from "../../../components/shared/DetailDrawer";
import CustomerInfoStrip from "../../inventory/customers/ModalTabs/CustomerInfoStrip";
import { customerDisplayName } from "../../../types/inventory/customerRecord";
import type { Patient } from "../../../types/healthcare/PatientTypes";

interface CustomerRecordDrawerProps {
	open: boolean;
	onClose: () => void;
	record: Patient;
}

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value).trim());

const shown = (value: unknown): string => text(value) || "—";

// CU-14ygtkje5r2: read-only and non-clinical; it issues no request and offers no write, whatever the viewer's permissions.
const CustomerRecordDrawer: React.FC<CustomerRecordDrawerProps> = ({ open, onClose, record }) => {
	const address = [record.address, record.city, record.state, record.postal_code, record.country]
		.map(text)
		.filter(Boolean)
		.join(", ");

	const items: DescriptionsProps["items"] = [
		{ key: "name", label: "Name", children: customerDisplayName(record) },
		{ key: "email", label: "Email", children: shown(record.email) },
		{ key: "phone", label: "Phone", children: shown(record.phone) },
		{ key: "address", label: "Address", children: address || "—" },
		{ key: "account", label: "Account type", children: "Customer" },
		{ key: "status", label: "Account status", children: record.is_active === false ? "Deactivated" : "Active" },
	];

	return (
		<DetailDrawer
			open={open}
			onClose={onClose}
			title={customerDisplayName(record)}
			titleMeta={<CustomerInfoStrip customer={record} />}
			headerActions={<Tag color="blue">Customer</Tag>}
		>
			<div data-testid="customer-record-view">
				<Alert
					type="info"
					showIcon
					message="Customer account — not a patient record"
					description="Clinical records and Healthcare Identifier actions are available only for patients. Customer accounts are managed from Inventory → Customers."
				/>
				<Descriptions column={1} bordered size="small" style={{ marginTop: 16 }} items={items} />
			</div>
		</DetailDrawer>
	);
};

export default CustomerRecordDrawer;
